import _ from 'lodash';
import { nanoid } from 'nanoid';
import {
  ENABLED_PC_MODES,
  PLATFORM_COST_MODE,
} from 'Views/Common/enum';

// Stable, client-only row id used as the React key (tiers have no natural
// stable identity — first_unit/last_unit get recomputed on edit/removal, and
// the index shifts when a row is removed from the middle). Never sent to the
// API (buildPlatformCostPayload only picks first_unit/last_unit/amount) and
// must be stripped before any equality/dirty comparison — see omitTierIds.
export const nextTierId = () => nanoid();

// Default two-row tier table (first tier starts at 0 so the platform cost is
// charged even with no subscribers). Returned from a factory so each caller
// gets a fresh array — the value object is used directly as component state and
// must never be mutated in place.
export const getDefaultTiers = () => [
  { _id: nextTierId(), first_unit: 0, last_unit: 10, amount: 0 },
  { _id: nextTierId(), first_unit: 11, last_unit: null, amount: 0 },
];

export const PERCENT_PAYOUT_TYPE = { label: '%', value: 'percent' };

export const getPayoutTypes = (symbol = '$') => [
  { label: symbol, value: 'fixed' },
  PERCENT_PAYOUT_TYPE,
];

// Recompute each tier's first_unit from the previous row's last_unit. The first
// tier always starts at 0.
export const recomputeFirstUnits = (tiers) =>
  tiers.map((tier, index) => ({
    ...tier,
    first_unit: index === 0 ? 0 : (tiers[index - 1].last_unit || 0) + 1,
  }));

export const appendTierRow = (tiers) => {
  const lastRow = tiers[tiers.length - 1];

  if (lastRow.last_unit != null) {
    return [
      ...tiers,
      { _id: nextTierId(), first_unit: lastRow.last_unit + 1, last_unit: null, amount: 0 },
    ];
  }

  const newLastUnitForPrev = lastRow.first_unit + 1;
  return [
    ...tiers.slice(0, -1),
    { ...lastRow, last_unit: newLastUnitForPrev },
    { _id: nextTierId(), first_unit: newLastUnitForPrev + 1, last_unit: null, amount: 0 },
  ];
};

// The flat value object driving <PlatformCostFields />.
// `pcSavedMode` is not a form field — it remembers the mode the API has stored
// (null until the group has a config) so a disabled config can be saved and
// re-enabled without losing its tier data. See getSavedMode.
export const getDefaultPlatformCost = () => ({
  pcMode: PLATFORM_COST_MODE.NONE,
  pcSavedMode: null,
  pcLevel: 'group',
  pcChargeDay: 1,
  pcFlatAmount: 1,
  pcTiers: getDefaultTiers(),
  pcStartDate: '',
  pcLocationIds: [],
  revshareEnabled: false,
  revshareId: '',
  revshareType: 'fixed',
  revshareValue: 1,
});

// Map a platform-cost config from the API into the flat value object.
//
// A disabled config still keeps its mode, tiers, billing start date and
// locations on the API side, so everything is mapped regardless of `enabled` —
// switching the dropdown from "None" back to Flat/Tier then shows what was last
// configured instead of empty defaults. Only the selected mode reflects
// `enabled`.
export const mapPlatformCostConfig = (cfg = {}) => {
  const pc = getDefaultPlatformCost();

  if (ENABLED_PC_MODES.includes(cfg.mode)) {
    pc.pcMode = cfg.enabled ? cfg.mode : PLATFORM_COST_MODE.NONE;
    pc.pcSavedMode = cfg.mode;
    pc.pcStartDate = cfg.start_date || '';
    pc.pcLocationIds = (cfg.locations || [])
      .filter((l) => l.enabled)
      .map((l) => l.location_id);
  }

  // Only the saved mode's amounts are stored, so the other mode keeps its
  // default (a flat config has no tier table, and vice versa).
  if (cfg.mode === PLATFORM_COST_MODE.FLAT && cfg.tiers?.[0]?.amount) {
    pc.pcFlatAmount = cfg.tiers[0].amount / 100;
  } else if (cfg.mode === PLATFORM_COST_MODE.TIER && cfg.tiers?.length) {
    pc.pcTiers = cfg.tiers.map((t) => ({ ...t, _id: nextTierId(), amount: t.amount / 100 }));
  }

  pc.pcLevel = cfg.level || 'group';
  pc.pcChargeDay = cfg.charge_day || 1;
  pc.revshareEnabled = Boolean(cfg.revshare_id);
  pc.revshareId = cfg.revshare_id || '';
  pc.revshareType = cfg.revshare_type || 'fixed';
  pc.revshareValue = cfg.revshare_value || 1;

  return pc;
};

// The mode the API stores for a value object. "None" only turns the config off,
// so the stored mode stays whatever was last saved — flat for a group that has
// no config yet.
export const getSavedMode = (pc) => {
  if (pc.pcMode !== PLATFORM_COST_MODE.NONE) return pc.pcMode;
  return ENABLED_PC_MODES.includes(pc.pcSavedMode)
    ? pc.pcSavedMode
    : PLATFORM_COST_MODE.FLAT;
};

// After a successful save, reset the fields that are irrelevant to the saved
// mode so the snapshot/dirty comparison is stable and matches what a reload of
// the config would map to.
export const normalizePlatformCost = (pc) => {
  const savedMode = getSavedMode(pc);
  return {
    ...pc,
    pcSavedMode: savedMode,
    ...(savedMode === PLATFORM_COST_MODE.FLAT
      ? { pcTiers: getDefaultTiers() }
      : { pcFlatAmount: 1 }),
  };
};

// Strips the client-only tier `_id` before any equality/dirty comparison —
// two independently-built pc objects (e.g. separate default-tier calls) can
// have the same logical tiers but different `_id`s.
export const omitTierIds = (pc) => ({
  ...pc,
  pcTiers: pc.pcTiers.map(({ _id, ...rest }) => rest),
});

// True when the only difference between current and snapshot is the selected
// locations (lets the caller use the lighter locations-only endpoint).
export const isOnlyLocationsChanged = (current, snapshot) => {
  if (!snapshot || current.pcLevel !== 'location') return false;
  if (!ENABLED_PC_MODES.includes(current.pcMode)) return false;
  const { pcLocationIds: currLocs, ...restCurr } = omitTierIds(current);
  const { pcLocationIds: snapLocs, ...restSnap } = omitTierIds(snapshot);
  return _.isEqual(restCurr, restSnap) && !_.isEqual(currLocs, snapLocs);
};

// Tiers for the API. Flat mode is a single open-ended tier starting at 0 so the
// cost applies even with no subscribers (matches the tier table's first row).
const buildTiers = (pc, mode) => {
  if (mode === PLATFORM_COST_MODE.FLAT) {
    return [{ first_unit: 0, last_unit: null, amount: Math.round(pc.pcFlatAmount * 100) }];
  }
  return pc.pcTiers.map((t) => ({
    first_unit: t.first_unit,
    last_unit: t.last_unit,
    amount: Math.round(t.amount * 100),
  }));
};

/**
 * Build the API payload for upsertPlatformCostConfig from the flat value object.
 *
 * Selecting "None" only flips `enabled` off — the API validates the mode's
 * tier/level/location rules on every upsert regardless of `enabled`, so a
 * disable can't send an empty flat tier. It resends the stored mode's
 * configuration (see getSavedMode) with `enabled: false`.
 */
export const buildPlatformCostPayload = (groupId, pc) => {
  const base = {
    group_id: groupId,
    level: pc.pcLevel,
    charge_day: pc.pcChargeDay,
    revshare_id: pc.revshareEnabled ? pc.revshareId : null,
    revshare_type: pc.revshareEnabled ? pc.revshareType : null,
    revshare_value: pc.revshareEnabled ? pc.revshareValue : null,
  };

  const mode = getSavedMode(pc);

  return {
    ...base,
    enabled: pc.pcMode !== PLATFORM_COST_MODE.NONE,
    mode,
    start_date: pc.pcStartDate || null,
    tiers: buildTiers(pc, mode),
    location_ids: pc.pcLevel === 'location' ? pc.pcLocationIds : [],
  };
};
